<template>
  <div class="landmark-search-input">
    <b-input-group>
      <b-form-input
        ref="searchInput"
        v-model="searchQuery"
        :placeholder="placeholder"
        :state="state"
        @focus="showDropdown = true"
        @input="onSearchInput"
        @keydown.down="onKeyDownDown"
        @keydown.up="onKeyDownUp"
        @keydown.enter="onKeyDownEnter"
        @keydown.esc="onKeyDownEsc"
      />
      <b-input-group-append>
        <b-button v-if="selectedLandmark" variant="outline-secondary" @click="clearSelection" title="Clear">
          <i class="ri-close-line"></i>
        </b-button>
        <b-button @click="onSelectOnMap" title="Select on Map">
          <i class="ri-map-pin-line"></i> Map
        </b-button>
      </b-input-group-append>
    </b-input-group>

    <!-- Dropdown -->
    <div v-show="showDropdown && filteredLandmarks.length > 0" class="search-dropdown">
      <div
        v-for="(landmark, index) in filteredLandmarks"
        :key="landmark.lndmId"
        class="dropdown-item"
        :class="{ active: index === highlightedIndex }"
        @click="selectLandmark(landmark)"
      >
        <div class="item-content">
          <i class="ri-map-pin-2-line item-icon"></i>
          <div class="item-text">
            <div class="item-name">{{ landmark.displayName }}</div>
            <div class="item-meta">
              <span :class="['type-badge', landmark.lndmType]">{{ landmark.lndmType || 'N/A' }}</span>
              <span class="coordinates">{{ formatCoordinates(landmark.lndmLatitude, landmark.lndmLongitude) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Results -->
    <div v-show="showDropdown && searchQuery && filteredLandmarks.length === 0" class="search-dropdown no-results">
      <div class="no-results-text">
        <i class="ri-search-line"></i>
        <span>No landmarks found. Try "Select on Map"</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LandmarkSearchInput',
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    label: {
      type: String,
      default: 'Landmark',
    },
    landmarks: {
      type: Array,
      default: () => [],
    },
    state: {
      type: Boolean,
      default: null,
    },
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  data() {
    return {
      searchQuery: '',
      showDropdown: false,
      highlightedIndex: 0,
      searchDebounce: null,
    };
  },
  computed: {
    selectedLandmark() {
      if (!this.value) return null;
      // eslint-disable-next-line eqeqeq
      return this.landmarks.find((l) => l.lndmId == this.value);
    },
    placeholder() {
      return `Search ${this.label.toLowerCase()}...`;
    },
    filteredLandmarks() {
      if (!this.searchQuery) return this.landmarks.slice(0, 10);
      const query = this.searchQuery.toLowerCase();
      return this.landmarks
        .filter((l) => {
          const name = (l.lndmName || '').toLowerCase();
          const displayName = (l.displayName || '').toLowerCase();
          const type = (l.lndmType || '').toLowerCase();
          return name.includes(query) || displayName.includes(query) || type.includes(query);
        })
        .slice(0, 10);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const landmark = this.selectedLandmark;
          if (landmark) {
            this.searchQuery = landmark.displayName;
          }
        } else {
          this.searchQuery = '';
        }
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  },
  methods: {
    onSearchInput() {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }
      this.searchDebounce = setTimeout(() => {
        this.highlightedIndex = 0;
      }, 300);
    },
    selectLandmark(landmark) {
      this.$emit('input', landmark.lndmId);
      this.searchQuery = landmark.displayName;
      this.showDropdown = false;
    },
    clearSelection() {
      this.$emit('input', null);
      this.searchQuery = '';
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    },
    onSelectOnMap() {
      this.$emit('select-on-map');
    },
    onKeyDownDown() {
      if (this.highlightedIndex < this.filteredLandmarks.length - 1) {
        this.highlightedIndex++;
      }
    },
    onKeyDownUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      }
    },
    onKeyDownEnter() {
      if (this.filteredLandmarks[this.highlightedIndex]) {
        this.selectLandmark(this.filteredLandmarks[this.highlightedIndex]);
      }
    },
    onKeyDownEsc() {
      this.showDropdown = false;
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showDropdown = false;
      }
    },
    formatCoordinates(lat, lng) {
      if (!lat || !lng) return '';
      return `${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}`;
    },
  },
};
</script>

<style scoped>
.landmark-search-input {
  position: relative;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  margin-top: 0.25rem;
}

.dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.dropdown-item:hover,
.dropdown-item.active {
  background: #f8f9fa;
}

.item-content {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.item-icon {
  font-size: 1.25rem;
  color: #0d6efd;
  flex-shrink: 0;
}

.item-text {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-weight: 500;
  color: #212529;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
}

.type-badge {
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  font-weight: 600;
}

.type-badge.port {
  background: #e3f2fd;
  color: #1976d2;
}

.type-badge.facility {
  background: #f3e5f5;
  color: #7b1fa2;
}

.type-badge.default {
  background: #e9ecef;
  color: #495057;
}

.coordinates {
  color: #6c757d;
  font-family: monospace;
}

.no-results {
  padding: 1rem;
  text-align: center;
}

.no-results-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.no-results-text i {
  font-size: 1.5rem;
}
</style>
